import { useMemo, useRef, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';
import { ChartComponentProps, BarChartHookReturn } from './types';
import { handleSegmentClick, getRecursiveSelection } from './utils';
import { generateStackedChartData } from './dataUtils';
import { createChartOptions } from './chartConfig';
import { AggregatedOrderViewType } from 'src/gql/graphql';

export const useStackedHorizontalBarChart = ({
  table,
  grouping,
  rowSelection,
  setRowSelection,
  borderColor,
  textColor,
}: ChartComponentProps): BarChartHookReturn => {
  const chartRef = useRef<any>(null);

  // Add custom click listener for y-axis labels (horizontal)
  useEffect(() => {
    const handleCanvasClick = (event: MouseEvent) => {
      if (chartRef.current) {
        const chart = chartRef.current;
        const rect = chart.canvas.getBoundingClientRect();
        const canvasPosition = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };
        const dataY = chart.scales.y.getValueForPixel(canvasPosition.y);

        // Check if click is in the y-axis label area (left side)
        const chartArea = chart.chartArea;
        const isInLabelArea = canvasPosition.x < chartArea.left;

        if (
          isInLabelArea &&
          dataY !== null &&
          dataY !== undefined &&
          dataY >= 0
        ) {
          const dataIndex = Math.round(dataY);
          const tableRows = table.getRowModel().rows;

          if (dataIndex < tableRows.length) {
            const parentRow = tableRows[dataIndex];
            const shiftPressed = event.shiftKey;

            if (shiftPressed) {
              const isCurrentlySelected =
                table.getState().rowSelection[parentRow.id] ?? false;
              if (isCurrentlySelected) {
                const entriesToRemove = Object.fromEntries(
                  getRecursiveSelection(parentRow).map(([id]) => [id, false])
                );
                setRowSelection((prev) => ({ ...prev, ...entriesToRemove }));
              } else {
                const entriesToAdd = Object.fromEntries(
                  getRecursiveSelection(parentRow)
                );
                setRowSelection((prev) => ({ ...prev, ...entriesToAdd }));
              }
            } else {
              const entries = Object.fromEntries(
                getRecursiveSelection(parentRow)
              );
              setRowSelection(entries);
            }
          }
        }
      }
    };

    const canvas = chartRef.current?.canvas;
    if (canvas) {
      canvas.addEventListener('click', handleCanvasClick);
      return () => {
        canvas.removeEventListener('click', handleCanvasClick);
      };
    }
  }, [table, setRowSelection]);

  const chartData = useMemo(() => {
    return generateStackedChartData(
      table,
      grouping,
      rowSelection,
      AggregatedOrderViewType.StackedHorizontalBarChart
    ) as ChartData<'bar'>;
  }, [
    table.options.data,
    table.getState().columnFilters,
    table.getState().grouping,
    grouping,
    rowSelection,
  ]);

  const chartOptions = useMemo((): ChartOptions<'bar'> => {
    return createChartOptions(
      AggregatedOrderViewType.StackedHorizontalBarChart,
      borderColor,
      textColor,
      table,
      rowSelection,
      setRowSelection,
      handleSegmentClick
    ) as ChartOptions<'bar'>;
  }, [
    borderColor,
    textColor,
    table.options.data,
    table.getState().columnFilters,
    table.getState().grouping,
    rowSelection,
    setRowSelection,
  ]);

  return {
    chartData,
    chartOptions,
    chartRef,
  };
};

export default function StackedHorizontalBarChart(props: ChartComponentProps) {
  const { chartData, chartOptions, chartRef } =
    useStackedHorizontalBarChart(props);

  return <Bar data={chartData} options={chartOptions} ref={chartRef as any} />;
}
